import { LOGO_FULL, LOGO_SYMBOL } from '@zuruflex/brand';
import { cn } from '../../lib/cn.js';

export interface LogoProps {
  /**
   * `full` is the whole name, the symbol standing in for its Z; `mark` is the
   * symbol alone, for the spots too small for a word — a loader, an avatar.
   */
  variant?: 'full' | 'mark';
  className?: string;
  title?: string;
}

/**
 * The Zuruflex logo on the web: no plate, drawn straight on the page. It is
 * all shapes — the lettering is outlined in `@zuruflex/brand` — so no font is
 * involved. Its three colours come from the theme (`--logo-*`): "Zuru" and
 * the closing full stop are yellow in both themes; on a light page the Z's
 * dot and "flex" are black, on a dark one they are white.
 */
export function Logo({ variant = 'full', className, title = 'Zuruflex' }: LogoProps) {
  // Colours go through `style`, not SVG attributes: Safari ignores a CSS
  // variable in a presentation attribute and paints the fallback, which put
  // black letters on the dark theme.
  // The Z alone is drawn heavier than the Z that starts the full word.
  const { dot, path, strokeWidth } = variant === 'mark' ? LOGO_SYMBOL : LOGO_FULL.symbol;
  const symbol = (
    <>
      <circle cx={dot.cx} cy={dot.cy} r={dot.r} style={{ fill: 'var(--logo-dot, #0A0A0A)' }} />
      <path
        d={path}
        fill="none"
        style={{ stroke: 'var(--logo-z, #FFE500)' }}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </>
  );

  if (variant === 'mark') {
    return (
      <svg viewBox={LOGO_SYMBOL.viewBox} className={cn('size-8', className)} role="img" aria-label={title}>
        {symbol}
      </svg>
    );
  }

  return (
    <svg viewBox={LOGO_FULL.viewBox} className={cn('h-7 w-auto', className)} role="img" aria-label={title}>
      <g transform={LOGO_FULL.symbolTransform}>{symbol}</g>
      <path d={LOGO_FULL.uru} style={{ fill: 'var(--logo-z, #FFE500)' }} />
      <path d={LOGO_FULL.flex} style={{ fill: 'var(--logo-type, #0A0A0A)' }} />
      <circle
        cx={LOGO_FULL.stop.cx}
        cy={LOGO_FULL.stop.cy}
        r={LOGO_FULL.stop.r}
        style={{ fill: 'var(--logo-z, #FFE500)' }}
      />
    </svg>
  );
}
